// src/pages/analytics/ArRollup.tsx
//
// A/R aging rolled up by salesman / area / customer category — the PBI
// "AR by Salesman" page (flag analytics.arsalesman). Built client-side from
// the A/R Aging customer list, so it follows every filter on that tab and
// needs no agent change. Click a row to filter the customer table to it.

import type { ARCustomerDetail } from '../../types';

const fmtCur = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);

export type ArRollupDim = 'salesman' | 'area' | 'category';

interface Agg { key: string; customers: number; balance: number; current: number; over30: number; over60: number; over90: number }

function rollup(customers: ARCustomerDetail[], dim: ArRollupDim): Agg[] {
  const by = new Map<string, Agg>();
  for (const c of customers) {
    const key = ((dim === 'salesman' ? c.salesman : dim === 'area' ? c.area : c.category) || '').trim() || '(none)';
    const a = by.get(key) ?? { key, customers: 0, balance: 0, current: 0, over30: 0, over60: 0, over90: 0 };
    a.customers += 1;
    a.balance += c.balance;
    a.current += c.current;
    a.over30 += c.over30;
    a.over60 += c.over60;
    a.over90 += c.over90 + c.over120;   // 90+ = over90 + over120, as on the customer table
    by.set(key, a);
  }
  return [...by.values()].sort((x, y) => y.balance - x.balance);
}

const LABELS: Record<ArRollupDim, string> = { salesman: 'Salesman', area: 'Area', category: 'Category' };

export function ArRollup({ customers, dim, onDim, selected, onSelect }: {
  customers: ARCustomerDetail[];
  dim: ArRollupDim;
  onDim: (d: ArRollupDim) => void;
  selected: string;
  onSelect: (key: string) => void;
}) {
  const rows = rollup(customers, dim);
  const total = rows.reduce((t, r) => ({
    ...t, customers: t.customers + r.customers, balance: t.balance + r.balance, current: t.current + r.current,
    over30: t.over30 + r.over30, over60: t.over60 + r.over60, over90: t.over90 + r.over90,
  }), { key: 'Total', customers: 0, balance: 0, current: 0, over30: 0, over60: 0, over90: 0 });
  const pct90 = (a: Agg) => (a.balance > 0 ? (a.over90 / a.balance) * 100 : 0);
  const th = 'px-3 py-2.5 text-right text-slate-400 font-medium whitespace-nowrap';
  const td = 'px-3 py-2 text-right tabular-nums whitespace-nowrap';

  return (
    <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-slate-700/40">
        <h3 className="text-sm font-semibold text-white">A/R by {LABELS[dim]}</h3>
        <div className="flex items-center gap-1.5">
          {(Object.keys(LABELS) as ArRollupDim[]).map(d => (
            <button key={d} onClick={() => onDim(d)}
              className={`px-2 py-1 text-[11px] rounded-md border transition-all ${
                dim === d ? 'bg-blue-500/15 text-blue-200 border-blue-500/40'
                          : 'border-slate-700/50 text-slate-500 hover:text-slate-300 bg-slate-900/40'}`}>
              {LABELS[d]}
            </button>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto max-h-80 overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0">
            <tr className="border-b border-slate-700/50 bg-slate-800">
              <th className="px-3 py-2.5 text-left text-slate-400 font-medium">{LABELS[dim]}</th>
              <th className={th}>Customers</th>
              <th className={th}>Balance</th>
              <th className={th}>Current</th>
              <th className={th}>31–60</th>
              <th className={th}>61–90</th>
              <th className={th}>90+</th>
              <th className={th}>% 90+</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.key} onClick={() => onSelect(selected === r.key ? '' : r.key)}
                className={`border-b border-slate-800/50 cursor-pointer transition-colors ${selected === r.key ? 'bg-blue-500/10' : 'hover:bg-slate-700/20'}`}>
                <td className="px-3 py-2 text-white whitespace-nowrap">{r.key}</td>
                <td className={`${td} text-slate-400`}>{r.customers}</td>
                <td className={`${td} text-white`}>{fmtCur(r.balance)}</td>
                <td className={`${td} text-slate-300`}>{fmtCur(r.current)}</td>
                <td className={`${td} text-slate-300`}>{fmtCur(r.over30)}</td>
                <td className={`${td} text-amber-300`}>{fmtCur(r.over60)}</td>
                <td className={`${td} text-red-300`}>{fmtCur(r.over90)}</td>
                <td className={`${td} ${pct90(r) >= 28 ? 'text-red-400 font-medium' : 'text-slate-400'}`}>{pct90(r).toFixed(1)}%</td>
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-slate-600/60 bg-slate-800/60 font-semibold">
                <td className="px-3 py-2 text-white">Total</td>
                <td className={`${td} text-slate-300`}>{total.customers}</td>
                <td className={`${td} text-white`}>{fmtCur(total.balance)}</td>
                <td className={`${td} text-white`}>{fmtCur(total.current)}</td>
                <td className={`${td} text-white`}>{fmtCur(total.over30)}</td>
                <td className={`${td} text-white`}>{fmtCur(total.over60)}</td>
                <td className={`${td} text-white`}>{fmtCur(total.over90)}</td>
                <td className={`${td} text-white`}>{pct90(total).toFixed(1)}%</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <div className="px-3 py-2 border-t border-slate-700/30 text-[11px] text-slate-500">
        Follows the filters above · click a row to show only its customers · % 90+ at or over 28% is red
      </div>
    </div>
  );
}
